import { hhmmToUTCMinutes, getIanaTzName } from '@util/helpers/datetime.helpers';
import { patch } from '@services/query-services/default.query-service';

export async function addDigestConfigToNewUser(user) {
  const digestConfig = user?.digest_configs;
  const tenant = user?.tenants[0];
  const tzId = user?.time_zone_id ?? tenant?.time_zone_id;
  const ianaTzName = getIanaTzName(tzId);

  if (!digestConfig?.morning && !digestConfig?.evening) {
    return patch({
      model: 'people',
      id: user?.id,
      body: {
        digest_configs: {
          morning: { time_of_day: hhmmToUTCMinutes('07:00', ianaTzName) },
          evening: { time_of_day: hhmmToUTCMinutes('19:00', ianaTzName) },
          last_minute_prep: false
        }
      }
    });
  }
}

/**
 * Generates a cryptographically secure password using the Web Crypto API.
 * This function creates a random 12-character password suitable for temporary use.
 *
 * @returns {string} A secure random password string
 */
export function generateSecurePassword() {
  // Generate a cryptographically secure password using Web Crypto API
  const array = new Uint8Array(16); // 128-bit random array
  crypto.getRandomValues(array);

  // Convert to base64-like string and make it more readable
  return Array.from(array, (byte) => byte.toString(36))
    .join('')
    .slice(0, 12);
}

/**
 * Checks if activating a user would exceed the tenant's license limit.
 * License limits only apply to non-Enterprise tenants.
 *
 * @param {Object} options - The options object
 * @param {Object} options.tenant - The tenant object containing license information
 * @param {number} options.currentActiveCount - Current count of active users
 * @param {Function} options.isEnterpriseUser - Function to check if user is enterprise
 * @returns {Object} Object containing validation results and data for UI feedback
 */
export function checkLicenseLimitForActivation({ tenant, currentActiveCount, isEnterpriseUser }) {
  // License limits don't apply to Enterprise tenants
  if (isEnterpriseUser()) {
    return {
      isLimitApplicable: false,
      isActiveSwitchDisabled: false,
      wouldExceedLimit: false,
      licenseLimit: 0,
      currentActiveCount: 0
    };
  }

  const licenseLimit = tenant?.internal_config?.num_of_licenses || 0;
  const wouldExceedLimit = currentActiveCount >= licenseLimit;
  const isActiveSwitchDisabled = wouldExceedLimit;

  return {
    isLimitApplicable: true,
    isActiveSwitchDisabled,
    wouldExceedLimit,
    licenseLimit,
    currentActiveCount
  };
}
